import { createContext, useContext, useEffect, useState, useCallback } from "react";
import {
  api,
  getUserToken,
  setUserToken,
  clearUserToken,
  getActiveWorkspaceId,
  setActiveWorkspaceId,
  clearActiveWorkspaceId,
} from "../lib/api";
import i18n, { normalizeLang } from "../i18n";

const applyUserLocale = (u) => {
  // L'utilisateur authentifié impose sa locale persistée (priorité la plus haute).
  if (u && u.locale) {
    const l = normalizeLang(u.locale);
    if (i18n.language !== l) i18n.changeLanguage(l);
    try { localStorage.setItem("securepass_lang", l); } catch (e) { /* noop */ }
  }
};

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWorkspaceId, setActiveWs] = useState(getActiveWorkspaceId());

  const loadWorkspaces = useCallback(async () => {
    try {
      const { data } = await api.get("/workspaces");
      setWorkspaces(data);
      const current = getActiveWorkspaceId();
      const valid = data.find((w) => w.id === current);
      if (!valid) {
        const personal = data.find((w) => w.is_personal) || data[0];
        if (personal) {
          setActiveWorkspaceId(personal.id);
          setActiveWs(personal.id);
        }
      }
      return data;
    } catch (e) {
      setWorkspaces([]);
      return [];
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!getUserToken()) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/account/me");
      setUser(data.user);
      applyUserLocale(data.user);
      await loadWorkspaces();
    } catch (e) {
      clearUserToken();
      clearActiveWorkspaceId();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [loadWorkspaces]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (token, u) => {
    setUserToken(token);
    setUser(u);
    applyUserLocale(u);
    await loadWorkspaces();
  };

  const logout = () => {
    clearUserToken();
    clearActiveWorkspaceId();
    setUser(null);
    setWorkspaces([]);
    setActiveWs(null);
  };

  const switchWorkspace = (id) => {
    setActiveWorkspaceId(id);
    setActiveWs(id);
    // Persiste le dernier workspace utilisé côté backend (§4). Best-effort.
    api.post(`/account/workspaces/${id}/activate`).catch(() => {});
  };

  const activeWorkspace =
    workspaces.find((w) => w.id === activeWorkspaceId) ||
    workspaces.find((w) => w.is_personal) ||
    workspaces[0] ||
    null;

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        loading,
        login,
        logout,
        refresh,
        workspaces,
        loadWorkspaces,
        activeWorkspaceId,
        activeWorkspace,
        switchWorkspace,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
